import Head from "next/head";
import React, { useEffect, useState } from "react";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Slider from "react-slick";
import config from "../../../config";
import { ImageList } from "../../../src/components/design-pages/imageList";

const tileData = [
  {
    src: "https://images.orientbell.com/media/design-images/kitchen/Peninsula Kitchen 0/Modern Peninsula Kitchen Design with Wooden Cabinets.jpg",
    alt: "Modern Peninsula Kitchen Design with Wooden Cabinets",
    title: "Modern Peninsula Kitchen Design with Wooden Cabinets",
  },
  {
    src: "https://images.orientbell.com/media/design-images/kitchen/Peninsula Kitchen 0/Sleek Peninsula Kitchen Design with Dark Wood Cabinets.jpg",
    alt: "Sleek Peninsula Kitchen Design with Dark Wood Cabinets",
    title: "Sleek Peninsula Kitchen Design with Dark Wood Cabinets",
  },
  {
    src: "https://images.orientbell.com/media/design-images/kitchen/Peninsula Kitchen 0/Elegant Peninsula Kitchen Design with Marble Wall Tiles.jpg",
    alt: "Elegant Peninsula Kitchen Design with Marble Wall Tiles",
    title: "Elegant Peninsula Kitchen Design with Marble Wall Tiles",
  },
];

const text =
  "A peninsula kitchen layout perfectly blends style and functionality, offering a seamless way to cook, dine, and connect. Unlike a standalone kitchen island, a peninsula extends from existing cabinetry. This creates extra countertops for meal prep while doubling as a cozy spot for casual seating. From hosting friends to enjoying a quiet cup of coffee, the peninsula layout maximises space. Their versatility also enhances the overall aesthetic of your kitchen. With smart peninsula kitchen designs, you can craft a layout that improves both efficiency and comfort, making every moment in the kitchen feel effortless.";
const settings = {
  infinite: true,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: true,
  centerMode: false,
  autoplaySpeed: 3000,
  arrows: true,
  dots: false,
  centerPadding: "0px",
  responsive: [
    {
      breakpoint: 991,
      settings: {
        arrows: true,
        centerMode: false,
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        arrows: true,
        centerMode: false,
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 641,
      settings: {
        arrows: true,
        centerMode: false,
        slidesToShow: 1,
      },
    },
  ],
};
const faqs = [
  {
    question: "What is a peninsula kitchen layout?",
    answer:
      "A peninsula kitchen layout includes a connected countertop that extends from existing cabinets or walls. It works like a kitchen island but is attached on one side, providing extra storage, seating, and workflow improvement.",
  },
  {
    question: "Are peninsula kitchens in style?",
    answer:
      "Yes, kitchen peninsula designs remain a popular choice in modern kitchens and open kitchen designs. They improve space efficiency, offer additional storage, and create a natural division between the cooking and living areas. Their versatility makes them ideal for both small and spacious kitchens.",
  },
  {
    question: "What size should a kitchen peninsula be?",
    answer:
      "The ideal kitchen peninsula size varies depending on the available layout. Generally, it should be 48 to 72 inches long and 24 to 36 inches deep for sufficient workspace. If it includes seating, ensure an overhang of at least 15 inches for comfortable legroom.",
  },
];

function PeninsulaKitchen() {
  const maxChars = 300;
  const previewText = text.substring(0, maxChars);
  const remainingText = text.substring(maxChars);

  const [isReadMore, setIsReadMore] = useState(false);
  const [isExpertTalkClicked, setIsExpertTalkClicked] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [relatedBlogData, setRelatedBlogData] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const displayedFAQs = showAll ? faqs : faqs.slice(0, 5);

  var requestOptions = {
    method: "GET",
  };
  const getRelatedBlogs = async () => {
    fetch(
      `${config.HEADLESS_BASE_URL}/blog/wp-json/post/v2/category/term=kitchen-designs`,
      requestOptions
    )
      .then((response) => response.text())
      .then((result) => {
        setRelatedBlogData(JSON.parse(result));
      })
      .catch((error) => console.log("error", error));
  };
  const handleToggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  useEffect(() => {
    getRelatedBlogs();
  }, []);

  return (
    <>
      <Head>
        <title>Peninsula Kitchen Design Ideas | Orientbell </title>

        <meta
          name="title"
          content="Peninsula Kitchen Design Ideas | Orientbell "
        />
        <meta
          name="description"
          content="Looking for Peninsula Kitchen ideas? Orientbell offers creative ideas to craft warm, modern, and inviting kitchen spaces."
        />
      </Head>

      <div className="designpage-section">
        <div className="container">
          <Breadcrumb>
            <Breadcrumb.Item href="https://www.orientbell.com">
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item href="https://www.orientbell.com/design-ideas">
              Design Ideas
            </Breadcrumb.Item>
            <Breadcrumb.Item href="https://www.orientbell.com/design-ideas/kitchen-designs">
              Kitchen Designs
            </Breadcrumb.Item>
            <Breadcrumb.Item href="https://www.orientbell.com/design-ideas/kitchen-designs/peninsula-kitchen">
              Peninsula Kitchen
            </Breadcrumb.Item>
          </Breadcrumb>
          <div className="designpage-heading-bx padding-spacing">
            <h4>Showing {tileData.length} results for </h4>
            <h1>Peninsula Kitchen</h1>
            <p className="description-text">
              {previewText}
              {!isReadMore && text.length > maxChars && "..."}
              {isReadMore && <span>{remainingText}</span>}
              {text.length > maxChars && (
                <button onClick={() => setIsReadMore(!isReadMore)}>
                  {isReadMore ? "Read Less" : "Read More"}
                </button>
              )}
            </p>
          </div>
          <div className="obl-master-design-section">
            <h2>Peninsula Modular Kitchen Gallery</h2>
            <div className="designpage-category-row inner-design-gallery">
              <ImageList tileData={tileData} />
            </div>

            <div className="bottom-content">
              <div className="common-text-content-section">
                <h2>Popular Peninsula Kitchen Layout</h2>
                <p>
                  A kitchen peninsula is known to offer extra countertops,
                  seating, and storage while maintaining an open feel. Below are
                  some of the popular kitchen peninsula designs that create
                  efficient layouts suited for different kitchen sizes and
                  needs:
                </p>
                <h5>
                  <strong>L-Shaped Peninsula</strong>
                </h5>
                <p>
                  An L-shaped kitchen peninsula extends from the main counter at
                  a right angle, naturally dividing the cooking and dining
                  areas. One side provides a spacious countertop for meal prep,
                  while the other includes seating. This layout works well in
                  open-plan kitchens, keeping the space connected yet organised.
                </p>
                <h5>
                  <strong>U-Shaped Peninsula</strong>
                </h5>
                <p>
                  A U-shaped kitchen peninsula surrounds the cook on three
                  sides, offering extensive countertop space, cabinetry, and a
                  well-defined cooking zone. The open side allows easy movement,
                  while the extended peninsula creates a breakfast bar for
                  dining. This layout maximises storage and ensures a smooth
                  workflow.
                </p>
                <h5>
                  <strong>Galley Peninsula with Seating </strong>
                </h5>
                <p>
                  A galley-style kitchen peninsula features a narrow, elongated
                  layout with a peninsula extending from one side. One end is
                  used for food preparation, while the other includes seating
                  for casual meals. This design optimises space efficiency and
                  creates a streamlined workflow, making it ideal for smaller
                  kitchens requiring functionality and style.
                </p>
              </div>

              <div className="common-text-content-section">
                <h2>Why Choose a Kitchen Peninsula Design?</h2>
                <p>
                  A peninsula kitchen layout is a brilliant addition to any
                  home. Whether in a spacious modular or compact space, a
                  kitchen peninsula design enhances efficiency and usability.
                  Here’s why it’s a great choice:
                </p>
                <h5>
                  <strong>Maximises Space Efficiency </strong>
                </h5>
                <p>
                  A kitchen peninsula extends the counter without requiring
                  extra walls, making it perfect for small kitchens. It
                  optimises the available layout, ensuring smooth movement and
                  better organisation.
                </p>
                <h5>
                  <strong>Enhances Storage Options</strong>
                </h5>
                <p>
                  Unlike a standalone kitchen island, a peninsula kitchen layout
                  integrates seamlessly with existing cabinetry. It offers
                  additional drawers, shelves, and cupboards, helping to keep
                  essentials within easy reach.
                </p>
                <h5>
                  <strong>Adds Extra Seating</strong>
                </h5>
                <p>
                  A kitchen peninsula design provides a built-in seating area,
                  making it ideal for casual dining or entertaining. It also
                  creates a cosy breakfast nook while maintaining an open feel.
                </p>
                <h5>
                  <strong>Improves Workflow and Functionality</strong>
                </h5>
                <p>
                  A peninsula streamlines workflow by creating an efficient work
                  triangle between the sink, stove, and refrigerator. This makes
                  meal preparation smoother, especially in a modern kitchen
                  design setup.
                </p>
                <h5>
                  <strong>Supports an Open-Concept Layout</strong>
                </h5>
                <p>
                  A peninsula kitchen layout is perfect for open-plan homes,
                  acting as a subtle divider between the cooking and living
                  spaces. An L-shape kitchen design with a peninsula further
                  enhances the open-concept layout, blending seamlessly with the
                  rest of the home.
                </p>
              </div>

              <div className="common-text-content-section">
                <h2>Tips to Choose the Right Peninsula Kitchen Design</h2>
                <p>
                  Selecting the perfect peninsula kitchen design requires a
                  balance of style and practicality. Whether designing a modular
                  kitchen or revamping an existing layout, these tips will help
                  in making the right choice:
                </p>
                <h5>
                  <strong>Consider the Layout</strong>
                </h5>
                <p>
                  The L-shaped layout works well for an open kitchen design,
                  seamlessly connecting the cooking area with the dining or
                  living space. In this layout, opting for the same floor tiles
                  in both the kitchen and living space can eliminate visual
                  barriers. It creates a seamless flow between different zones,
                  ensuring a harmonious aesthetic while simplifying flooring
                  selection and reducing material waste. A U-shaped peninsula is
                  ideal for larger kitchens, offering ample counter space and
                  improving workflow.
                </p>
                <h5>
                  <strong>Prioritise Storage and Cabinet</strong>
                </h5>
                <p>
                  A well-planned peninsula kitchen design maximises storage with
                  built-in cabinets and drawers. Custom shelving and pull-out
                  compartments improve organisation in a modern kitchen design.
                </p>
                <h5>
                  <strong>Choose the Right Countertop Material</strong>
                </h5>
                <p>
                  Durable countertops, such as granite tile countertops, enhance
                  both aesthetics and functionality. The choice of materials
                  should blend with the kitchen floor tiles and kitchen wall
                  tiles for a cohesive look.
                </p>
                <h5>
                  <strong>Optimise Lighting for Better Visibility</strong>
                </h5>
                <p>
                  Pendant lights or under-cabinet LED strips highlight the
                  countertops and improve functionality. Proper lighting adds
                  warmth and enhances the overall peninsula kitchen design.
                </p>
                <h5>
                  <strong>Ensure Space Optimisation</strong>
                </h5>
                <p>
                  A peninsula should enhance space optimisation without making
                  the kitchen feel cramped. Keeping enough clearance around the
                  cabinets and seating area improves movement and accessibility.
                  Also, use large tiles in the kitchen to enhance the sense of
                  space, making the area appear more expansive and cohesive.
                </p>
              </div>
            </div>

            <div className="designpage-faq-section padding-spacing">
              <h2>FAQs About Peninsula Kitchen Design</h2>
              <div className="newacd designpage-faq-row">
                <ul className="accordion">
                  {displayedFAQs.map((faq, index) => (
                    <li key={index}>
                      <a className="toggle" onClick={() => handleToggle(index)}>
                        {index + 1}. {faq.question}
                      </a>
                      {activeIndex === index && (
                        <ul className="inner">
                          <li>{faq.answer}</li>
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
                {faqs.length > 5 && (
                  <div className="tru_btn_new design-page">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAll(!showAll)}
                    >
                      {showAll ? "Show Less" : "Read More"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {relatedBlogData?.length > 0 && (
              <div className="tile-related-blog">
                <h2 className="title">Related Blogs</h2>
                <div className="designpage-relatedpost-row">
                  <Slider {...settings}>
                    {relatedBlogData?.map((obj, index) => {
                      return (
                        <div className="related-blog-col" key={index}>
                          <div className="blog-figure">
                            <a href={obj.permalink}>
                              <img
                                src={obj.image}
                                loading="lazy"
                                alt={obj.title}
                              />
                            </a>
                          </div>
                          <div className="blog-text">
                            <div className="listingDate">{obj.date}</div>
                            <div className="listingHead">
                              <a href={obj.permalink}>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: obj.title,
                                  }}
                                />
                              </a>
                            </div>
                            <div className="listingText">
                              <p>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: obj.content.slice(0, -11),
                                  }}
                                />
                              </p>
                              <div className="listingButton">
                                <a href={obj.permalink}>Read More</a>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </Slider>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default PeninsulaKitchen;
